/**
 * Mahjong — motion vocabulary.
 *
 * A mahjong tile is a different object from a playing card: it is HEAVY, a
 * little ceramic, with weight and a hard edge. It does not skate across felt
 * like Solitaire's cards; it presses down, settles with a short solid "set,"
 * and when a pair clears it sinks and fades rather than flying. These springs
 * are tuned for that denser, calmer object — deliberately distinct from the
 * Solitaire card springs and the Five letterpress-tile springs.
 *
 * Everything collapses to ~instant under reduced-motion (the renderer reads
 * `useReducedMotion` and passes a `reduced` flag).
 */

/** A tile pressing under the finger on selection — a small, weighted dip. */
export const pressSpring = {
  type: 'spring' as const,
  stiffness: 540,
  damping: 30,
  mass: 0.9,
};

/** A selected tile lifting a touch off the table — heavier rise than a card. */
export const liftSpring = {
  type: 'spring' as const,
  stiffness: 320,
  damping: 24,
  mass: 1.1,
};

/** The whole board settling in on entry / deal. */
export const boardSpring = {
  type: 'spring' as const,
  stiffness: 180,
  damping: 26,
  mass: 1.15,
};

/** Hub cards. */
export const cardSpring = {
  type: 'spring' as const,
  stiffness: 230,
  damping: 26,
  mass: 0.95,
};

/** Hover lift on hub cards / buttons. */
export const hoverSpring = {
  type: 'spring' as const,
  stiffness: 340,
  damping: 22,
};

/** A gentle "not those two" nudge — a soft horizontal sway, never a hard shake. */
export const nudgeSpring = {
  type: 'spring' as const,
  stiffness: 600,
  damping: 16,
  mass: 0.8,
};

/** The win "settling garden" — tiles sink slow and luminous, unhurried. */
export const settleSpring = {
  type: 'spring' as const,
  stiffness: 120,
  damping: 24,
  mass: 1.2,
};

// ---------------------------------------------------------------------------
// Timing tokens (ms) — hand-tuned; collapse to ~0 under reduced-motion.
// ---------------------------------------------------------------------------

/** Press feedback on a tile / control. */
export const PRESS_MS = 100;
/** A matched pair clearing — the sink + fade. */
export const CLEAR_MS = 440;
/** Stagger between tiles as the board settles in on a fresh deal. */
export const DEAL_STAGGER_MS = 12;
/** The reshuffle re-settle — tiles lift, swap, and set back down. */
export const RESHUFFLE_MS = 520;
/** Win: time between each tile sinking away into the closing garden. */
export const WIN_STAGGER_MS = 26;
/** Win: one tile's sink-and-fade duration. */
export const WIN_SINK_MS = 760;
/** Beat before the completion card rises after the board has cleared. */
export const WIN_CARD_DELAY_MS = 1400;
/** Hub entry stagger between cards (seconds, for motion `delay`). */
export const HUB_STAGGER_S = 0.08;
